import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Button, Select, message, Empty } from "antd";
import {
  useActivePaymentMethodsQuery,
  useActiveBankAccountsQuery,
  useActiveReturnTypesQuery,
  useActiveRefundTypeQuery,
  useOrderByIdQuery,
  useOrdersRequest,
  useCreateOrderReturnMutation,
} from "../../Services";
import { useCart } from "../../Context";
import { RefundType } from "../../Enums";
import { OrderReturnForm } from "./OrderReturnForm";
import { drawerTitle } from "../AppUi";

interface OrderReturnDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface ReturnItemRow {
  order_item_id: string;
  product_id: string;
  stock_id: string | null;
  product_name: string;
  unit?: string;
  batch_no?: string;
  price: number;
  quantity: number;
  max_quantity: number;
  return_type_id: string;
  stock_adjusted: number;
  refund_price: number;
  total_refund: number;
}

export const OrderReturnDrawer: React.FC<OrderReturnDrawerProps> = ({
  open,
  onClose,
}) => {
  const { setExchangeDiscount } = useCart();
  const [searchTerm, setSearchTerm] = useState("");
  const [orderResults, setOrderResults] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [returnItems, setReturnItems] = useState<ReturnItemRow[]>([]);
  const [refundMethodId, setRefundMethodId] = useState<string | null>(null);
  const [refundTypeId, setRefundTypeId] = useState<string | null>(null);
  const [refundBankAccountId, setRefundBankAccountId] = useState<string | null>(
    null,
  );
  const [note, setNote] = useState("");

  const { data: paymentMethods = [] } = useActivePaymentMethodsQuery({
    enabled: open,
  });

  const { data: bankAccounts = [] } = useActiveBankAccountsQuery({
    enabled: open,
  });

  const { data: returnTypes = [] } = useActiveReturnTypesQuery({
    enabled: open,
  });

  const searchOrdersMutation = useOrdersRequest();

  const { data: refundTypes = [], isFetching: loadingRefundTypes } =
    useActiveRefundTypeQuery({
      enabled: open,
    });

  const { data: orderDetails, isFetching: loadingOrderDetails } =
    useOrderByIdQuery(selectedOrderId || "", {
      enabled: open && !!selectedOrderId,
    });

  const totalReturnValue = useMemo(
    () =>
      returnItems.reduce(
        (sum, item) => sum + item.refund_price * item.quantity,
        0,
      ),
    [returnItems],
  );

  const refundableAmount = useMemo(() => {
    const dueAmount = orderDetails?.due_amount ?? 0;
    const dueAdjustment = Math.min(dueAmount, totalReturnValue);
    return parseFloat((totalReturnValue - dueAdjustment).toFixed(2));
  }, [orderDetails?.due_amount, totalReturnValue]);

  const returnMutation = useCreateOrderReturnMutation();

  useEffect(() => {
    if (open) {
      setSearchTerm("");
      handleSearchOrders("");
    } else {
      resetDrawer();
    }
  }, [open]);

  useEffect(() => {
    if (paymentMethods.length > 0 && !refundMethodId) {
      setRefundMethodId(paymentMethods[0].id);
    }
    if (refundTypes.length > 0 && !refundTypeId) {
      setRefundTypeId(refundTypes[0]?.id || null);
    }
  }, [paymentMethods, refundMethodId, refundTypes]);

  useEffect(() => {
    if (orderDetails) {
      setSelectedOrderId(orderDetails.id);
      initializeReturnItems(orderDetails);
    }
  }, [orderDetails, returnTypes]);

  const handleSearchOrders = async (value: string) => {
    setSearchTerm(value);

    try {
      const res = await searchOrdersMutation.mutateAsync({
        page: 1,
        limit: 10,
        search: value.trim() || undefined,
      });
      setOrderResults(res?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const buildReturnItems = (order: any): ReturnItemRow[] => {
    const existingReturnMap: Record<string, number> = {};
    (order.returns || []).forEach((returnEntry: any) => {
      (returnEntry.items || []).forEach((item: any) => {
        existingReturnMap[item.order_item_id] =
          (existingReturnMap[item.order_item_id] || 0) + item.quantity;
      });
    });

    return (order.items || []).map((item: any) => {
      const alreadyReturned = existingReturnMap[item.id] ?? 0;
      const maxQuantity = Math.max(0, item.quantity - alreadyReturned);
      return {
        order_item_id: item.id,
        product_id: item.product_id,
        stock_id: item.stock_id || null,
        product_name: item.product_name || item.title || "",
        unit: item.unit || "",
        batch_no: item.batch_no || "",
        price: item.price || 0,
        quantity: 0,
        max_quantity: maxQuantity,
        return_type_id: returnTypes[0]?.id || "",
        stock_adjusted: item.stock_id ? 1 : 0,
        refund_price: item.price || 0,
        total_refund: 0,
      };
    });
  };

  const resetDrawer = () => {
    setSelectedOrderId(null);
    setReturnItems([]);
    setRefundMethodId(paymentMethods[0]?.id || null);
    setRefundTypeId(refundTypes[0]?.id || null);
    setRefundBankAccountId(null);
    setNote("");
    setSearchTerm("");
  };

  const initializeReturnItems = (order: any) => {
    const items = buildReturnItems(order);
    setReturnItems(items);
  };

  const handleReturnQtyChange = (index: number, value: number | null) => {
    const qty =
      value === null
        ? 0
        : Math.max(0, Math.min(value, returnItems[index]?.max_quantity ?? 0));
    setReturnItems((prev) =>
      prev.map((item, idx) =>
        idx !== index
          ? item
          : {
              ...item,
              quantity: qty,
              total_refund: parseFloat((item.refund_price * qty).toFixed(2)),
            },
      ),
    );
  };

  const handleReturnTypeChange = (index: number, value: string) => {
    setReturnItems((prev) =>
      prev.map((item, idx) =>
        idx !== index
          ? item
          : {
              ...item,
              return_type_id: value,
            },
      ),
    );
  };

  const handleStockAdjustedChange = (index: number, value: number) => {
    setReturnItems((prev) =>
      prev.map((item, idx) =>
        idx !== index
          ? item
          : {
              ...item,
              stock_adjusted: value,
            },
      ),
    );
  };

  const chosenOrder = orderDetails;

  const returnRows = returnItems.filter((item) => item.quantity > 0);

  const canSubmit =
    chosenOrder &&
    returnRows.length > 0 &&
    (refundableAmount <= 0 ||
      refundTypeId !== RefundType.CASH_REF ||
      Boolean(refundMethodId));

  const handleSubmit = () => {
    if (!chosenOrder) {
      message.warning("Select an invoice before submitting a return.");
      return;
    }

    if (returnRows.length === 0) {
      message.warning("Select at least one item quantity to return.");
      return;
    }

    if (
      refundableAmount > 0 &&
      refundTypeId === RefundType.CASH_REF &&
      !refundMethodId
    ) {
      message.warning("Select a refund method for the return.");
      return;
    }

    if (returnRows.some((item) => !item.return_type_id)) {
      message.warning("Select a return type for each item.");
      return;
    }

    const payload = {
      order_id: chosenOrder.id,
      total_refunded: refundableAmount,
      refund_payment_method_id:
        refundableAmount > 0 &&
        refundTypeId === RefundType.CASH_REF
          ? refundMethodId
          : null,
      refund_type_id: refundTypeId || null,
      bank_account_id:
        refundableAmount > 0 &&
        refundTypeId === RefundType.CASH_REF
          ? refundBankAccountId || null
          : null,
      note: note || null,
      items: returnRows.map((item) => ({
        order_item_id: item.order_item_id,
        product_id: item.product_id,
        stock_id: item.stock_id,
        quantity: item.quantity,
        refund_price: item.refund_price,
        total_refund: item.total_refund,
        return_type_id: item.return_type_id,
        stock_adjusted: item.stock_adjusted,
      })),
    };

    returnMutation.mutate(payload, {
      onSuccess: (res: any) => {
        if (res?.success) {
          message.success(res.message || "Return processed successfully.");
          if (payload.refund_type_id === RefundType.ITEM_EXCHANGE) {
            setExchangeDiscount(payload.total_refunded);
          }
          resetDrawer();
          onClose();
        } else {
          message.error(res?.message || "Failed to process order return.");
        }
      },
      onError: (error: any) => {
        message.error(error?.message || "Failed to process order return.");
      },
    });
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        resetDrawer();
        onClose();
      }}
      width={720}
      placement="right"
      title={drawerTitle(
        "Order Return",
        "Search invoices and select returned items.",
      )}
      footer={
        <div className="flex gap-2">
          <Button
            size="middle"
            className="flex-1 h-9 text-xs rounded-md border-gray-200 text-gray-500"
            onClick={() => {
              resetDrawer();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            size="middle"
            type="primary"
            disabled={!canSubmit}
            loading={returnMutation.isPending}
            onClick={handleSubmit}
            className="flex-1 h-9 text-xs font-semibold rounded-md"
          >
            Process Return
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className=" gap-4">
          <Select
            value={selectedOrderId}
            onChange={(value) => setSelectedOrderId(value)}
            placeholder="Search invoice number or customer"
            allowClear
            showSearch
            filterOption={false}
            onSearch={handleSearchOrders}
          >
            {orderResults?.map((order: any) => (
              <Select.Option key={order.id} value={order.id}>
                {order.order_no}
              </Select.Option>
            ))}
          </Select>

          <div className="space-y-">
            {selectedOrderId ? (
              <OrderReturnForm
                orderDetails={orderDetails}
                refundTypeId={refundTypeId}
                setRefundTypeId={setRefundTypeId}
                refundTypes={refundTypes}
                refundableAmount={refundableAmount}
                loadingOrderDetails={loadingOrderDetails}
                returnItems={returnItems}
                returnTypes={returnTypes}
                paymentMethods={paymentMethods}
                bankAccounts={bankAccounts}
                totalReturnValue={totalReturnValue}
                handleReturnQtyChange={handleReturnQtyChange}
                handleReturnTypeChange={handleReturnTypeChange}
                handleStockAdjustedChange={handleStockAdjustedChange}
                refundMethodId={refundMethodId}
                setRefundMethodId={setRefundMethodId}
                refundBankAccountId={refundBankAccountId}
                setRefundBankAccountId={setRefundBankAccountId}
                note={note}
                setNote={setNote}
              />
            ) : (
              <Empty description="Select an order from the search results to begin." />
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};
